/**
 * PR-30 — Zero Overlap Policy.
 * Single source of truth for which UI layers occupy the bottom area.
 * Lower-priority elements subscribe and receive an extra Y offset (px)
 * that pushes them above higher-priority active elements.
 *
 * Priority order (highest first):
 *   1. chat_input
 *   2. active_poll
 *   3. profile_quick
 *   4. bottom_nav
 *   5. floating_cta
 *   6. fab_secondary
 */
import { useSyncExternalStore } from "react";
import { trackEvent } from "@/lib/audit";

export type OverlapLayer =
  | "chat_input"
  | "active_poll"
  | "profile_quick"
  | "bottom_nav"
  | "floating_cta"
  | "fab_secondary";

export const OVERLAP_PRIORITY: OverlapLayer[] = [
  "chat_input",
  "active_poll",
  "profile_quick",
  "bottom_nav",
  "floating_cta",
  "fab_secondary",
];

const HEIGHTS: Record<OverlapLayer, number> = {
  chat_input: 56,
  active_poll: 140,
  profile_quick: 72,
  bottom_nav: 72,
  floating_cta: 56,
  fab_secondary: 56,
};

interface Snapshot {
  active: Record<OverlapLayer, boolean>;
  /** vertical offset px applied above the base bottom anchor for each layer */
  offsets: Record<OverlapLayer, number>;
}

const initial: Snapshot = {
  active: {
    chat_input: false, active_poll: false, profile_quick: false,
    bottom_nav: false, floating_cta: false, fab_secondary: false,
  },
  offsets: {
    chat_input: 0, active_poll: 0, profile_quick: 0,
    bottom_nav: 0, floating_cta: 0, fab_secondary: 0,
  },
};

let state: Snapshot = initial;
const listeners = new Set<() => void>();
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
const snap = () => state;

function recompute(active: Record<OverlapLayer, boolean>): Snapshot {
  const offsets = { ...initial.offsets };
  // For each layer, sum heights of all higher-priority layers that are active.
  for (let i = 0; i < OVERLAP_PRIORITY.length; i++) {
    const layer = OVERLAP_PRIORITY[i];
    let off = 0;
    for (let j = 0; j < i; j++) {
      const higher = OVERLAP_PRIORITY[j];
      if (active[higher]) off += HEIGHTS[higher];
    }
    offsets[layer] = off;
  }
  return { active, offsets };
}

export function setLayerActive(layer: OverlapLayer, on: boolean): void {
  if (state.active[layer] === on) return;
  const nextActive = { ...state.active, [layer]: on };
  const next = recompute(nextActive);
  const conflict = next.offsets[layer] > 0 || Object.values(next.offsets).some((v) => v > 0);
  state = next;
  if (conflict) {
    void trackEvent("overlap_prevented", { layer, on, offsets: next.offsets });
  }
  listeners.forEach((l) => l());
}

export function useOverlapState(): Snapshot {
  return useSyncExternalStore(subscribe, snap, snap);
}

export function useLayerOffset(layer: OverlapLayer): number {
  const s = useOverlapState();
  return s.offsets[layer];
}

export const OVERLAP_HEIGHTS = HEIGHTS;